<template>
  <div
    v-if="dhmeProjectAssemblyPlanningStatus === 'success'"
    class="d-flex flex-column flex-scroll-width"
  >
    <module-navigation-bar title="Project assembly planning">
      <template slot="module-nav-logo">
        <img
          v-if="isDaiwaLicense"
          alt=""
          height="40px"
          src="https://www.daiwahousemodular.eu/wp-content/uploads/daiwa_logo.svg"
        />
      </template>
    </module-navigation-bar>
    <div
      class="ant-glass-background d-flex radius-0 ant-border-bottom align-center px-2"
      style="height: 70px"
    >
      <div class="d-flex c-pointer">
        <div class="mx-2">
          <v-tooltip bottom>
            <template #activator="{ on }">
              <div style="font-size: 14px" v-on="on">
                <v-icon class="mr-1" dense> mdi-factory</v-icon>
                {{ dhmeProjectAssemblyHall?.hall ?? '-' }}
              </div>
            </template>
            <span>Hall</span>
          </v-tooltip>
        </div>
        <div class="mx-2">
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on"> mdi-cog</v-icon>
            </template>
            <span>Configuration</span>
          </v-tooltip>
          {{ dhmeProjectAssemblyHallConfiguration?.title ?? '-' }}
        </div>
        <div class="mx-2">
          <v-tooltip bottom>
            <template #activator="{ on }">
              <div style="font-size: 14px" v-on="on">
                <v-icon class="mr-1" dense>
                  mdi-clock-time-seven-outline
                </v-icon>
                {{ dhmeProjectAssemblyHallTime ?? '-' }}
              </div>
            </template>
            <span>{{ assemblyStartDate }}</span>
          </v-tooltip>
        </div>
        <div class="mx-2">
          <v-tooltip bottom>
            <template #activator="{ on }">
              <div style="font-size: 14px" v-on="on">
                <v-icon class="mr-1" dense> mdi-speedometer</v-icon>
                {{ dhmeProjectAssemblyHallSpeed ?? '-' }}
              </div>
            </template>
            <span>Workload</span>
          </v-tooltip>
        </div>
      </div>

      <v-spacer />
      <v-select
        v-model="highlightedModuleType"
        :items="moduleTypes"
        class="mx-5"
        clearable
        dense
        filled
        hide-details
        placeholder="Type"
        prepend-inner-icon="mdi-format-color-highlight"
        style="max-width: 200px"
      />
      <v-tooltip bottom>
        <template #activator="{ on: tooltip }">
          <v-btn icon tile @click="sendRequestPlanning" v-on="tooltip">
            <v-icon small>mdi-calendar-clock</v-icon>
          </v-btn>
        </template>
        <span>{{ text('submitRequest') }}</span>
      </v-tooltip>
      <v-chip
        v-if="dhmeProjectAssemblyPhase?.task_type?.custom_1 === 'request'"
        color="warning"
        >Submitted
      </v-chip>
      <v-chip
        v-if="dhmeProjectAssemblyPhase?.task_type?.custom_1 === 'planned'"
        color="info"
        >Planned
      </v-chip>
      <v-chip
        v-if="dhmeProjectAssemblyPhase?.task_type?.custom_1 === 'rejected'"
        color="error"
        >Rejected
      </v-chip>
      <v-menu
        v-model="exportMenu"
        :close-on-content-click="false"
        offset-y
      >
        <template #activator="{ on, attrs }">
          <v-btn
            class="ml-4"
            color="primary"
            elevation="0"
            small
            v-bind="attrs"
            v-on="on"
          >
            <v-icon class="mr-2">mdi-export</v-icon>
            export
          </v-btn>
        </template>
        <v-card>
          <v-card-text class="pb-0">
            <v-checkbox
              v-model="onlyOpenReport"
              dense
              hide-details
              label="Only open tasks"
            />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              color="primary"
              elevation="0"
              small
              @click="requestAssemblyReport"
            >
              <v-icon class="mr-2">mdi-export</v-icon>
              export
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-menu>
    </div>
    <div v-if="modulesNotPlanned.length > 0" class="px-10 pt-4">
      <v-alert icon="mdi-alert" color="warning" small
        >The following modules are found within the tasks but not in the manual
        import
        <br />
        <v-chip v-for="module in modulesNotPlanned" :key="module" x-small>{{
          module
        }}</v-chip>
        <br />
        Please delete the tasks from the tasks list or contact your
        administrator
      </v-alert>
    </div>
    <div
      v-if="dhmeProjectAssemblyTasksStatus === 'success' || isInitialized"
      class="d-flex flex-column flex-1 flex-scroll-width overflow-x-auto"
    >
      <dhme-project-assembly-planning-automated
        v-if="dhmeProjectAssemblyHall?.automated === 1"
        :highlighted-module-type="highlightedModuleType"
      />
      <dhme-project-assembly-planning-manual
        v-else-if="dhmeProjectAssemblyHall?.automated === 0"
        :highlighted-module-type="highlightedModuleType"
      />
      <div v-else class="d-flex align-center justify-center font-italic flex-1">
        You can request a planning in the top right
      </div>
    </div>
    <div v-else class="d-flex justify-center align-center flex-1">
      <ant-loading />
    </div>
  </div>
  <div v-else class="d-flex justify-center align-center">
    <ant-loading />
  </div>
</template>

<script>
import ModuleNavigationBar from '@/components/Modules/ModuleNavigationBar.vue';
import { DHME_PROJECT_ASSEMBLY_PLANNING } from '@/modules/modules';
import { mapGetters } from 'vuex';
import AntLoading from '@/components/AntLoading.vue';
import moment from 'moment';
import DhmeProjectAssemblyPlanningAutomated from '@/modules/daiwa-house-modular-europe/ProjectAssemblyPlanning/DhmeProjectAssemblyPlanningAutomated.vue';
import DhmeProjectAssemblyPlanningManual from '@/modules/daiwa-house-modular-europe/ProjectAssemblyPlanning/DhmeProjectAssemblyPlanningManual.vue';
import { executeCustomModuleCall } from '@/services/api/module.api';
import { importTasksV2 } from '@/services/api/v2/connect_v2';

export default {
  name: 'DHMEProjectAssemblyPlanning',
  components: {
    DhmeProjectAssemblyPlanningManual,
    DhmeProjectAssemblyPlanningAutomated,
    AntLoading,
    ModuleNavigationBar,
  },
  data: () => {
    return {
      isInitialized: false,
      locationTypeFilter: {},
      highlightedModuleType: '',
      loadingExport: false,
      onlyOpenReport: false,
      exportMenu: false,
    };
  },
  computed: {
    ...mapGetters([
      'project',
      'dhmeProjectAssemblyPlanningStatus',
      'dhmeProjectAssemblyTasks',
      'dhmeProjectAssemblyTasksStatus',
      'dhmeProjectAssemblyPhase',
      'dhmeProjectAssemblyHall',
      'dhmeProjectAssemblyHallSpeed',
      'dhmeProjectAssemblyHallTime',
      'dhmeProjectAssemblyHallConfiguration',
      'dhmeProjectAssemblyHallLocations',
      'dhmeProjectAssemblyPlanningModules',
      'dhmeProjectAssemblyPlanningCalendar',
      'selectedLicense',
      'project',
      'authenticatedUser',
      'isDaiwaLicense',
    ]),
    assemblyStartDate() {
      if (this.dhmeProjectAssemblyPhase) {
        return moment(this.dhmeProjectAssemblyPhase.planned_start)
          .clone()
          .set({
            hour: this.dhmeProjectAssemblyHallTime,
            minute: 0,
            second: 0,
            millisecond: 0,
          })
          .format('YYYY-MM-DD HH:mm');
      }

      return moment().format('YYYY-MM-DD HH:MM');
    },
    moduleTypes() {
      return [
        ...new Set(
          this.dhmeProjectAssemblyPlanningModules.map((m) => m.module_type)
        ),
      ];
    },
    modulesNotPlanned() {
      const existingBuildNrs = this.dhmeProjectAssemblyPlanningModules.map(
        (m) => m.build_nr
      );

      const taskBuildNrs = this.dhmeProjectAssemblyTasks
        .filter((x) => x?.sbscode?.code)
        .map((x) => x?.sbscode?.code);

      return taskBuildNrs.filter((x) => !existingBuildNrs.includes(x)) ?? [];
    },
  },
  async mounted() {
    await this.$store.dispatch('fetchDhmeAssemblyPlanningTasks');
    this.isInitialized = true;
  },
  methods: {
    async requestAssemblyReport() {
      this.exportMenu = false;
      this.loadingExport = true;
      try {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        await executeCustomModuleCall(
          this.project.id,
          this.project.modules.find(
            (module) => module.route === DHME_PROJECT_ASSEMBLY_PLANNING
          ).id,
          'exportProjectAssemblyReport',
          {
            project: this.project.id,
            timezone: timezone,
            only_open: this.onlyOpenReport,
          }
        );
        this.$store.commit('showNotification', {
          content:
            'Assembly Report requested, Your download will be ready shortly',
          color: 'info',
        });
      } catch (e) {
        this.$store.commit('showNotification', {
          content: 'Something went wrong while exporting the assembly report',
          color: 'error',
        });
      } finally {
        this.loadingExport = false;
      }
    },
    async sendRequestPlanning() {
      const data = [
        {
          id: this.dhmeProjectAssemblyPhase.id,
          options: {
            custom_1: 'request',
          },
        },
      ];

      const result = await importTasksV2(data);

      // Normalize (array or single object)
      const tasks = Array.isArray(result) ? result : [result];

      this.$store.commit('dhme_assembly_tasks_update', tasks);

      this.$store.commit('showNotification', {
        content: 'Planning request submitted',
        color: 'success',
      });
    },
    text() {
      const [path, ...params] = arguments;
      return this.$t(`modules.projectAssemblyPlanning.${path}`, params);
    },
  },
};
</script>

<style lang="scss" scoped></style>
